// src/components/admin/AdminShell.tsx
import { ReactNode } from "react";
import {
    Box,
    Drawer,
    List,
    ListItemButton,
    ListItemText,
    Toolbar,
    Typography,
    IconButton,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { Link as RouterLink, useLocation } from "react-router-dom";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import LightModeIcon from "@mui/icons-material/LightMode";
import { useThemeMode } from "../../style/ThemeModeProvider";

const drawerWidth = 240;

const SidebarHeader = styled(Box)(({ theme }) => ({
    padding: theme.spacing(2),
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(1),
}));

interface AdminShellProps {
    children: ReactNode;
}

export default function AdminShell({ children }: AdminShellProps) {
    const location = useLocation();
    const isActive = (path: string) => location.pathname.startsWith(path);

    const { mode, toggle } = useThemeMode();

    return (
        <Box sx={{ display: "flex" }}>
            {/* Sidebar */}
            <Drawer
                variant="permanent"
                sx={{
                    width: drawerWidth,
                    flexShrink: 0,
                    "& .MuiDrawer-paper": (theme) => ({
                        width: drawerWidth,
                        boxSizing: "border-box",
                        backgroundColor: theme.palette.admin.sidebarBg,
                        color: theme.palette.admin.sidebarText,
                        borderRight: `1px solid ${theme.palette.admin.sidebarBorder}`,
                    }),
                }}
            >
                <Toolbar />

                <SidebarHeader>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        <Typography variant="subtitle1" fontWeight={600}>
                            Admin Panel
                        </Typography>

                        <IconButton
                            onClick={toggle}
                            aria-label="toggle theme"
                            size="small"
                            sx={{ color: "inherit" }}
                        >
                            {mode === "dark" ? <LightModeIcon fontSize="small" /> : <DarkModeIcon fontSize="small" />}
                        </IconButton>
                    </Box>

                    <Typography variant="caption" color="text.secondary">
                        Manage your recipe content
                    </Typography>
                </SidebarHeader>

                <List>
                    <ListItemButton
                        component={RouterLink}
                        to="/admin/categories"
                        selected={isActive("/admin/categories")}
                    >
                        <ListItemText primary="Categories" />
                    </ListItemButton>

                    <ListItemButton
                        component={RouterLink}
                        to="/admin/recipes"
                        selected={isActive("/admin/recipes")}
                    >
                        <ListItemText primary="Recipes" />
                    </ListItemButton>
                </List>
            </Drawer>

            {/* Main content */}
            <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
                <Toolbar />
                {children}
            </Box>
        </Box>
    );
}
